
import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Calculator, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';
import { calculatorCategories } from '@/lib/data/calculatorCategories';

interface SearchResult {
  id: string;
  title: string;
  description: string;
  category: string;
  path: string;
}

interface SearchDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Search dialog that dynamically pulls all calculators from category data.
 * No hardcoded list — always in sync with the category files.
 */
const SearchDialog: React.FC<SearchDialogProps> = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // Dynamically build search index from all category data
  const calculators: SearchResult[] = useMemo(() => {
    return calculatorCategories.flatMap(category =>
      category.calculators.map(calc => ({
        id: calc.id,
        title: calc.name,
        description: calc.description,
        category: category.name,
        path: `/calculators/${category.id}/${calc.id}`,
      }))
    );
  }, []);

  const filteredResults = useMemo(() => {
    if (!searchQuery.trim()) return [];

    const query = searchQuery.toLowerCase();
    return calculators.filter(calc =>
      calc.title.toLowerCase().includes(query) ||
      calc.description.toLowerCase().includes(query) ||
      calc.category.toLowerCase().includes(query)
    ).slice(0, 8); // Limit to 8 results for performance
  }, [searchQuery, calculators]);

  const handleResultClick = (path: string) => {
    navigate(path);
    onClose();
    setSearchQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && filteredResults.length > 0) {
      handleResultClick(filteredResults[0].path);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="w-5 h-5" />
            Search Calculators
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search for calculators, formulas, or tools..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="pl-10"
              autoFocus
            />
          </div>

          {searchQuery.trim() && (
            <ScrollArea className="max-h-96">
              <div className="space-y-2">
                {filteredResults.length > 0 ? (
                  filteredResults.map((result) => (
                    <div
                      key={`${result.category}-${result.id}`}
                      className="p-3 rounded-lg border hover:bg-accent cursor-pointer transition-colors group"
                      onClick={() => handleResultClick(result.path)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Calculator className="w-4 h-4 text-primary" />
                            <h3 className="font-medium group-hover:text-primary transition-colors">
                              {result.title}
                            </h3>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            {result.description}
                          </p>
                          <Badge variant="secondary" className="text-xs">
                            {result.category}
                          </Badge>
                        </div>
                        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No calculators found for "{searchQuery}"</p>
                    <p className="text-sm mt-1">Try searching for mortgage, BMI, age, or business loan</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}

          {!searchQuery.trim() && (
            <div className="py-8 text-center text-muted-foreground">
              <Calculator className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>Start typing to search {calculators.length} calculators</p>
              <p className="text-sm mt-1">Try: mortgage, BMI, retirement, or productivity</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchDialog;
